import React from 'react';
import { motion } from 'framer-motion';
import { Target, Users, Video, AlertTriangle, Shield, Mic, MessageSquare } from 'lucide-react';
import './GroyperSlide.css';

// Import images
import charlieKirkImg from '../images/images for slides/charlie-kirk.jpg';
import nickFuentesImg from '../images/images for slides/Nick Fuentes .jpg';

function GroyperSlide({ showNotes }) {
  const strategies = [
    { icon: '🎤', text: 'Q&A-Trolling: "Fangfragen" bei konservativen Events' },
    { icon: '📱', text: '"Ratio"-Attacken: Online-Mobbing gegen Moderate' },
    { icon: '🎭', text: 'Ironie als Schutzschild: "Plausible Deniability"' },
    { icon: '✝️', text: '"Optics War": Anzug & Kreuz statt Nazi-Ästhetik' },
  ];

  return (
    <div className="slide groyper-slide">
      <div className="groyper-layout">
        {/* Header */}
        <div className="groyper-header">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="section-label"
          >
            🇺🇸 USA
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="slide-title"
          >
            "Hostile Takeover" – Der Groyper War
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="slide-subtitle"
          >
            Wie Rechtsextreme den US-Konservatismus von innen unterwandern
          </motion.p>
        </div>

        {/* Main Content */}
        <div className="groyper-content">
          {/* Left Side - The Conflict */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="conflict-section"
          >
            <h3 className="section-title">Der Machtkampf</h3>
            
            <div className="versus-container">
              {/* Charlie Kirk */}
              <div className="person-card kirk">
                <div className="person-image">
                  <img src={charlieKirkImg} alt="Charlie Kirk" className="person-photo" />
                </div>
                <div className="person-info">
                  <h4>Charlie Kirk †</h4>
                  <span className="org">"Conservative Inc."</span>
                  <ul className="traits">
                    <li>Business-Konservatismus</li>
                    <li>Freier Markt & Steuersenkung</li>
                    <li>Pro-legale Einwanderung</li>
                  </ul>
                </div>
              </div>

              <div className="versus-badge">
                <span>VS</span>
              </div>

              {/* Nick Fuentes */}
              <div className="person-card fuentes">
                <div className="person-image">
                  <img src={nickFuentesImg} alt="Nick Fuentes" className="person-photo warning" />
                </div>
                <div className="person-info">
                  <h4>Nick Fuentes</h4>
                  <span className="org">"America First" / Groypers</span>
                  <ul className="traits danger">
                    <li>"Christian Nationalist"</li>
                    <li>Antisemit & Holocaust-Leugner</li>
                    <li>Ziel: Autoritärer Ethnostaat</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Strategy & Goal */}
          <div className="strategy-section">
            {/* Strategy */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="strategy-box"
            >
              <h3 className="section-title">Die Taktiken seit 2019</h3>
              <div className="strategy-list">
                {strategies.map((item, index) => (
                  <motion.div
                    key={index}
                    className="strategy-item"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <span className="strategy-icon">{item.icon}</span>
                    <span className="strategy-text">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Goal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="goal-box"
            >
              <Target size={20} className="goal-icon" />
              <div className="goal-content">
                <h4>Das Ziel: "Hostile Takeover"</h4>
                <p>Die GOP-Hülle übernehmen & nach <strong>Rechtsaußen</strong> transformieren.</p>
              </div>
            </motion.div>

            {/* Warning Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="warning-box"
            >
              <div className="warning-header">
                <AlertTriangle size={20} />
                <span>Der Durchbruch</span>
              </div>
              <div className="warning-content">
                <div className="date-badge">November 2022</div>
                <p>
                  <strong>Kanye West</strong> bringt Fuentes zum Dinner mit <strong>Trump</strong> in Mar-a-Lago.
                </p>
                <p className="conclusion">
                  Holocaust-Leugner am Tisch des Ex-Präsidenten – Brandmauer durchbrochen.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {showNotes && (
        <div className="speaker-notes">
          <strong>📚 Zusatzinfos – Die Strategie im Detail:</strong><br/><br/>
          
          <strong>Wer ist Nick Fuentes?</strong> Geboren 1998, mexikanisch-italienische Wurzeln. Nutzt seine Herkunft strategisch: 
          "Ich bin ein Nationalist, der zufällig weiß ist" – entkräftet Rassismusvorwürfe. Nach Charlottesville 2017 erkannte er: 
          Die "Optics" müssen stimmen. Anzug statt Uniform, "Christus ist König" statt Hakenkreuz. 
          Nennt sich selbst "Katholische Taliban".<br/><br/>
          
          <strong>Die Q&A-Falle:</strong> Groypers besuchen TPUSA-Events, sehen "normal" aus, stellen dann Fangfragen wie: 
          "Warum Milliarden an Israel, während unsere Grenzen offen sind?" – Sprecher sind in der Falle: 
          Ehrliche Antwort = extrem, Ausweichen = schwach.<br/><br/>
          
          <strong>"Plausible Deniability":</strong> Der berüchtigte "Keks-Witz" – Holocaust mit Keksbacken verglichen. 
          Bei Kritik: "War nur ein Witz!" So werden radikale Inhalte verbreitet, ohne juristisch greifbar zu sein.<br/><br/>
          
          <strong>Eigene Infrastruktur:</strong> Nach Deplatforming: Cozy.tv (eigene Streaming-Plattform), 
          Krypto-Spenden, AFPAC als Gegen-CPAC. Das System hat ihn nicht gestoppt – es hat ihn unabhängiger gemacht.<br/><br/>
          
          <strong>September 2025:</strong> Tod von Charlie Kirk → Machtvakuum. Fuentes' Reaktion: 
          "Bist du bereit zu sterben?" – Der Kulturkampf wird zum existenziellen Krieg stilisiert.
        </div>
      )}
    </div>
  );
}

export default GroyperSlide;
